import { useState } from "react";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { ConfirmDialog } from "@/components/common/Dialog";
import { deleteProjectService } from "@/api/projectServicesMaintenance/api";
import { showSuccessToast } from "@/hooks/use-toast";
import { ProjectService } from "@/types/domain";
import { LoadingOverlay } from "@/components/common/Loading/LoadingOverlay";

export type DeleteProjectServiceDialogProps = {
  service: ProjectService;
  callBack: () => Promise<void>;
}

export const DeleteProjectServiceDialog = ({ service, callBack }: DeleteProjectServiceDialogProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  let triggerTitle = "Delete this service";
  if (service.id === 0) {
    triggerTitle = "Cannot delete: This is the default service.";
  } else if (service.usageCount && service.usageCount > 0) {
    triggerTitle = `Cannot delete: This service is in use by ${service.usageCount} Projects / Epics.`;
  }

  const handleOpenChange = (open: boolean) => {
    setDialogOpen(open);
    setError(null);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    setError(null);
    try {
      await deleteProjectService(service.id);
      showSuccessToast("Service deleted successfully.");
      setDialogOpen(false);
      await callBack();
    } catch (err) {
      setError("Failed to delete service.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingOverlay 
        isVisible={loading} 
        message="Deleting service..." 
      />
      <ConfirmDialog
        open={dialogOpen}
        onOpenChange={handleOpenChange}
        triggerIcon={faTrashCan}
        triggerText=""
        triggerVariant="icon"
        disableTrigger={service.allowedToDelete === false}
        triggerTitle={triggerTitle}
        title="Delete Project Service"
        description={
          <>
            Are you sure you want to delete <strong>{service.name}</strong>?<br />
            This action cannot be undone.
            {error && (
              <div className="text-red-500 text-sm mt-2">{error}</div>
            )}
          </>
        }
        confirmText="Delete"
        cancelText="Cancel"
        loading={loading}
        onConfirm={handleConfirmDelete}
        disableConfirm={loading}
      />
    </>
  );
}